/**
 * Cliente HTTP Simplificado
 *
 * Función genérica para realizar peticiones HTTP con axios
 * Utilizada principalmente por el módulo SIGMA para OAuth y API calls
 */

import axios, { AxiosRequestConfig } from 'axios';

// ============================================================================
// TIPOS
// ============================================================================

interface HttpsParams {
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  url: string;
  body?: any;
  headers?: Record<string, string>;
  handleErrors?: boolean;
  name?: string;
  withCredentials?: boolean;
}

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

const MAX_ARRAY_ITEMS = 20;

/**
 * Resume objetos y arrays largos para evitar saturar los logs
 */
function summarize(obj: any): any {
  if (Array.isArray(obj)) {
    if (obj.length > MAX_ARRAY_ITEMS) {
      return [
        ...obj.slice(0, MAX_ARRAY_ITEMS),
        `...+${obj.length - MAX_ARRAY_ITEMS} more items`,
      ];
    }
    return obj;
  }
  if (typeof obj === 'object' && obj !== null) {
    const keys = Object.keys(obj);
    if (keys.length > MAX_ARRAY_ITEMS) {
      const summary: Record<string, any> = {};
      keys.slice(0, MAX_ARRAY_ITEMS).forEach((k) => (summary[k] = obj[k]));
      summary['...'] = `+${keys.length - MAX_ARRAY_ITEMS} more keys`;
      return summary;
    }
  }
  return obj;
}

/**
 * Registra peticiones HTTP en modo desarrollo
 */
function logRequest(params: {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: any;
  response?: any;
  status?: number;
  duration?: number;
  name?: string;
}): void {
  if (import.meta.env.MODE !== 'development') return;

  const { method, url, body, response, status, duration, name } = params;

  const methodIcons: Record<string, string> = {
    get: '📡',
    post: '✉️',
    put: '🛠️',
    delete: '🗑️',
    patch: '🩹',
  };
  const icon = methodIcons[method.toLowerCase()] || '🔹';

  const methodColors: Record<string, string> = {
    get: 'color: #28a745; font-weight:bold;',
    post: 'color: #ffc107; font-weight:bold;',
    put: 'color: #0d6efd; font-weight:bold;',
    patch: 'color: #9370db; font-weight:bold;',
    delete: 'color: #ff6b6b; font-weight:bold;',
  };
  const color = methodColors[method.toLowerCase()] || 'font-weight:bold;';

  const urlObj = new URL(url);
  const path = urlObj.pathname;

  console.groupCollapsed(
    `%c${icon} ${method.toUpperCase()} → ${path} [${status ?? 'N/A'}]${name ? ` [${name}]` : ''}`,
    color
  );

  console.log(
    '%c%c' + urlObj.origin + '%c' + path,
    'color: #999; font-weight:bold;',
    'color: #999;',
    'color: #0d6efd; font-weight:bold;'
  );

  if (body && method.toLowerCase() !== 'get') {
    console.log('📤 Body:', summarize(body));
  }

  if (response !== undefined) {
    console.log('📥 Response:', summarize(response));
  }

  if (duration !== undefined) {
    console.log('⏱️ Duration:', `${duration.toFixed(2)} s`);
  }

  console.groupEnd();
}

/**
 * Registra errores HTTP en consola
 */
function logError(error: any, params: { method: string; url: string; body?: any }): void {
  if (import.meta.env.MODE !== 'development') return;

  const { method, url, body } = params;
  const urlObj = new URL(url);
  const path = urlObj.pathname + urlObj.search;
  const status = error.response?.status ?? 'N/A';
  const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
  const errorData = error.response?.data;

  console.groupCollapsed(
    `%c❌ Error → ${method.toUpperCase()} ${path} [${status}]`,
    'color: #ff3b3b; font-weight:bold; font-size:13px'
  );

  console.error('%c🌐 Base URL:', 'color: gray; font-weight:bold;', urlObj.origin);
  console.log('%c🔸 Endpoint:', 'color: #0d6efd; font-weight:bold;', path);
  console.log('%c📊 Status:', 'color: #ff6b6b; font-weight:bold;', status);
  console.log('%c⚠️ Message:', 'color: #ffc107; font-weight:bold;', errorMsg);

  if (body && method.toLowerCase() !== 'get') {
    console.log('%c📤 Request Body:', 'color: #999;', summarize(body));
  }

  if (errorData && typeof errorData === 'object') {
    console.log('%c📥 Error Response:', 'color: #999;', summarize(errorData));
  }

  console.groupEnd();
}

// ============================================================================
// CLIENTE HTTP
// ============================================================================

/**
 * Cliente HTTP genérico basado en axios
 *
 * Realiza peticiones HTTP con logging automático en desarrollo
 *
 * @param params - Configuración de la petición
 * @returns Respuesta del servidor
 *
 * @example
 * ```ts
 * const data = await https({
 *   method: 'post',
 *   url: 'https://api.example.com/users',
 *   body: { name: 'John' },
 *   headers: { 'Authorization': 'Bearer token' }
 * });
 * ```
 */
export const https = async <T = any>(params: HttpsParams): Promise<T> => {
  const { method, url, body, headers = {}, handleErrors = true, name, withCredentials } = params;
  const start = performance.now();

  try {
    const config: AxiosRequestConfig = {
      method,
      url,
      headers,
      data: body,
      ...(withCredentials !== undefined && { withCredentials }),
    };

    const response = await axios<T>(config);
    const duration = (performance.now() - start) / 1000;

    logRequest({
      method,
      url,
      headers,
      body,
      response: response.data,
      status: response.status,
      duration,
      name,
    });

    return response.data;
  } catch (error: any) {
    if (!handleErrors) throw error;

    logError(error, { method, url, body });

    throw error;
  }
};
